import React, { useState } from "react";
import { Badge, Spinner } from "reactstrap";
import { api } from "../../../config";

// Helper function to format priority
const formatPriority = (priority) => {
    if (!priority) return '';

    const priorityMap = {
        'HIGH': 'High',
        'MEDIUM': 'Medium',
        'LOW': 'Low'
    };

    return priorityMap[priority] || priority;
};

const StatusToggleCell = ({ row, onStatusChange }) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleStatusToggle = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsLoading(true);
        try {
            await onStatusChange(row.id, !row.status);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            className="d-flex align-items-center justify-content-center w-100"
        >
            {isLoading ? (
                <Spinner size="sm" color="primary" />
            ) : (
                <div className="form-check form-switch m-0">
                    <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        id={`status-${row.id}`}
                        checked={row.status}
                        onChange={handleStatusToggle}
                        style={{ cursor: "pointer" }}
                        title={row.status ? "Active" : "Inactive"}
                    />
                </div>
            )}
        </div>
    );
};

export const getFarmhouseColumns = ({ onDeleteClick, onEditClick, onRowClick, onAddAmenitiesClick, onStatusChange, onHistoryClick }) => {
    return [
        {
            name: <span className="font-weight-bold fs-13"></span>,
            selector: (row) => row.images?.[0]?.imagePath,
            sortable: false,
            cell: (row) => {
                const sortedImages = row.images ? [...row.images].sort((a, b) => (a.priority || 0) - (b.priority || 0)) : [];
                const firstImage = sortedImages.length > 0 ? sortedImages[0] : null;


                if (!firstImage) {
                    return (
                        <div className="my-2" style={{ width: "45px", height: "45px", backgroundColor: "#f0f0f0", borderRadius: "5px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="ri-image-line text-muted"></i>
                        </div>
                    );
                }

                const imagePath = firstImage.imagePath || '';
                const baseImgUrl = api.IMG_URL;

                // Development proxy logic
                let baseUrl = baseImgUrl && baseImgUrl.endsWith('/') ? baseImgUrl.slice(0, -1) : (baseImgUrl || "");

                const path = imagePath && imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
                const imageUrl = `${baseUrl}${path}`;

                return (
                    <div onClick={() => onRowClick(row)} className="cursor-pointer py-2">
                        <img
                            src={imageUrl}
                            alt={row.name}
                            style={{ width: "45px", height: "45px", objectFit: "cover", borderRadius: "5px" }}
                        />
                    </div>
                );
            },
            width: "60px",
        },
        {
            name: <span className="font-weight-bold fs-13">Name</span>,
            selector: (row) => row.name,
            sortable: false,
            minWidth: "220px",
            grow: 2,
            cell: (row) => (
                <div
                    className="cursor-pointer w-100"
                    onClick={() => onRowClick(row)}
                >
                    <span className="fw-medium">{row.name}</span>
                    <br />
                    <span className="text-muted fs-11">{row.farmNo}</span>
                </div>
            ),
        },
        {
            name: <span className="font-weight-bold fs-13">Nearby</span>,
            selector: (row) => row.location?.nearby,
            sortable: false,
            minWidth: "140px",
            grow: 1.5,
            cell: (row) => (
                <div onClick={() => onRowClick(row)} className="cursor-pointer w-100">
                    {row.location?.nearby || "N/A"}
                </div>
            ),
        },
        {
            name: <span className="font-weight-bold fs-13">Priority</span>,
            selector: (row) => row.priority,
            sortable: false,
            width: "120px",
            cell: (row) => (
                <div onClick={() => onRowClick(row)} className="cursor-pointer w-100 text-center">
                    <Badge
                        color={
                            row.priority === "HIGH"
                                ? "danger"
                                : row.priority === "MEDIUM"
                                    ? "warning"
                                    : "success"
                        }
                    >
                        {formatPriority(row.priority)}
                    </Badge>
                </div>
            ),
        },
        // {
        //     name: <span className="font-weight-bold fs-13">Bedrooms</span>,
        //     selector: (row) => row.bedrooms,
        //     sortable: true,
        //     cell: (row) => (
        //         <div onClick={() => onRowClick(row)} className="cursor-pointer w-100">
        //             {row.bedrooms}
        //         </div>
        //     ),
        // },
        {
            name: <span className="font-weight-bold fs-13">Status</span>,
            selector: (row) => row.status,
            sortable: false,
            width: "110px",
            cell: (row) => <StatusToggleCell row={row} onStatusChange={onStatusChange} />,
            ignoreRowClick: false,
            allowOverflow: true,
            button: false,
        },
        {
            name: <span className="font-weight-bold fs-13">Actions</span>,
            width: "190px",
            cell: (row) => (
                <div className="d-flex align-items-center justify-content-center gap-2 farmhouse-action-group">
                    {!row.status && (
                        <div
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onAddAmenitiesClick && onAddAmenitiesClick(row);
                            }}
                            className="text-decoration-none px-1 py-2 cursor-pointer farmhouse-action-btn"
                            title="Add Amenities & Images"
                        >
                            <i className="ri-add-circle-fill fs-13 text-success p-2 rounded-circle shadow-sm bg-f1 farmhouse-action-icon" />
                        </div>
                    )}
                    <div
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onEditClick(row);
                        }}
                        className="text-decoration-none px-1 py-2 cursor-pointer farmhouse-action-btn"
                        title="Edit"
                    >
                        <i className="ri-pencil-fill fs-13 text-primary p-2 rounded-circle shadow-sm bg-f1 farmhouse-action-icon" />
                    </div>
                    <div
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onDeleteClick(row);
                        }}
                        className="text-decoration-none px-1 py-2 cursor-pointer farmhouse-action-btn"
                        title="Delete"
                    >
                        <i className="ri-delete-bin-7-fill fs-13 text-danger p-2 rounded-circle shadow-sm bg-f1 farmhouse-action-icon" />
                    </div>
                    <div
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onHistoryClick && onHistoryClick(row);
                        }}
                        className="text-decoration-none px-1 py-2 cursor-pointer farmhouse-action-btn"
                        title="Booking History"
                    >
                        <i className="ri-history-line fs-13 text-info p-2 rounded-circle shadow-sm bg-f1 farmhouse-action-icon" />
                    </div>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: false,
            button: false,
        },
    ];
};